import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as css from './fileupload.module.scss';
import Icon, { IconType } from '../icon';
import classNames from 'classnames';
import useDragOutsideElement from '../../hooks/useDragOutsideElement';
import { NotificationFunctionContext } from '../notifications/notificationProvider';
import { useIntl } from 'react-intl';
import translations from './fileupload.i18n';
import Button from '../button';

interface BasicFileUploadInterface {
  label: string | React.ReactNode;
  onChange: (file?: File) => void;
  id?: string;
  maxSizeInMB?: number;
  className?: string;
  initialFile?: File;
}

interface FileUploadInterface extends BasicFileUploadInterface {
  accept: string;
  hoverFileIcon?: IconType;
  fileUploadedIcon?: IconType;
}

const bytesFromMB = (MB: number) => MB * 1048576;

export const fileMIMETypesToFiletypes = (types: string[]): string[] =>
  types.map((type) => type.split('/')[1]);

/*
 * The component only supports single file upload at the moment
 * Handling of multiple files can be added, when needed
 * */
const SingleFileUpload: React.FC<
  React.PropsWithChildren<FileUploadInterface>
> = ({
  label,
  accept,
  id,
  hoverFileIcon,
  fileUploadedIcon,
  maxSizeInMB,
  onChange,
  className,
  initialFile,
}) => {
  const acceptList = accept.split(',');
  const [file, setFile] = useState<File | undefined>(initialFile);
  const [isHovering, setIsHovering] = useState<boolean>(false);

  const fileInputId = `fileinput-${id}`;
  const wrapperId = `fileinput-form-${id}`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragOutside = useDragOutsideElement(wrapperRef, true);

  const { addErrorNotification } = useContext(NotificationFunctionContext);
  const { formatMessage } = useIntl();
  const labels = useMemo(
    () => ({
      wrongType: formatMessage(
        { id: translations.wrongType.id },
        { types: fileMIMETypesToFiletypes(acceptList).join(', ') },
      ),
      tooLarge: formatMessage(
        { id: translations.tooLarge.id },
        { maxSize: maxSizeInMB },
      ),
    }),
    [formatMessage, acceptList, maxSizeInMB],
  );

  const updateFiles = (newFiles?: FileList, event?: React.SyntheticEvent) => {
    const firstItem =
      (newFiles?.length || 0) > 0 ? newFiles!.item(0)! : undefined;

    if (firstItem) {
      if (!acceptList.includes(firstItem.type)) {
        addErrorNotification(labels.wrongType);
        return;
      } else if (
        maxSizeInMB !== undefined &&
        firstItem.size > bytesFromMB(maxSizeInMB)
      ) {
        addErrorNotification(labels.tooLarge);
        return;
      }
    }

    if (event) {
      (event.currentTarget as HTMLElement).blur();
    }

    setFile(firstItem);
    onChange(firstItem);
  };

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    updateFiles(event.target.files || undefined, event);

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsHovering(false);
    updateFiles(event.dataTransfer.files, event);
  };

  useEffect(() => setIsHovering(!dragOutside), [dragOutside]);

  const fileUploaded = file !== undefined;

  const mainIconType = useMemo<IconType>(() => {
    if (isHovering) {
      return IconType.FileUploadActive;
    }

    return fileUploaded
      ? fileUploadedIcon || IconType.FileUploaded
      : IconType.FileUpload;
  }, [fileUploaded, isHovering, fileUploadedIcon]);

  return (
    <div
      id={wrapperId}
      className={classNames(
        css.fileUploadWrapper,
        isHovering && css.isHovering,
        className,
      )}
      ref={wrapperRef}
      // Prevents file being opened in new tab (browser default), instead of being sent to <input> on drop
      onDragOver={(event) => event.preventDefault()}
      onDrop={onDrop}
      onDragEnd={() => setIsHovering(false)}
    >
      <div className={css.iconOuterContainer}>
        <div className={css.iconInnerContainer}>
          <Icon className={css.cloud} type={mainIconType} />
          <Icon
            className={classNames(css.file, isHovering && css.isHovering)}
            type={hoverFileIcon || IconType.GenericFileOpaque}
          />
        </div>
      </div>
      <input
        id={fileInputId}
        type="file"
        accept={accept}
        multiple={false}
        name={file?.name}
        onChange={onInputChange}
      />
      <label htmlFor={fileInputId}>{label}</label>
      <UploadedFile name={file?.name} remove={updateFiles} />
    </div>
  );
};

interface UploadedFileInterface {
  remove: () => void;
  name?: string;
}

const UploadedFile: React.FC<
  React.PropsWithChildren<UploadedFileInterface>
> = ({ name, remove }) => {
  const { formatMessage } = useIntl();
  const formattedName = formatMessage(
    { id: translations.formattedName.id },
    { name },
  );
  const removeBtn = formatMessage({ id: translations.removeBtn.id });

  const onClick = (event: React.SyntheticEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (
      (event as React.KeyboardEvent<HTMLButtonElement>).key === 'Enter' ||
      event.type === 'click'
    ) {
      remove();
    }
  };

  return (
    <div className={css.uploadedFile}>
      {name && (
        <>
          <p className={css.filename}>{formattedName}</p>
          <Button.Remove title={removeBtn} onClick={onClick} />
        </>
      )}
    </div>
  );
};

export const LabelUploadLink: React.FC<
  React.PropsWithChildren<{ uploadLinkLabel: string }>
> = ({ uploadLinkLabel }) => {
  return <span className={css.uploadLink}>{uploadLinkLabel}</span>;
};

export const PDPFileUpload: React.FC<
  React.PropsWithChildren<BasicFileUploadInterface>
> = ({ ...props }) => (
  <SingleFileUpload
    hoverFileIcon={IconType.PDFFileOpaque}
    fileUploadedIcon={IconType.PDFFileUploaded}
    accept={'application/pdf'}
    {...props}
  />
);

export default SingleFileUpload;
